import React, { useState, useEffect } from 'react'
import { FAQEditor } from '@/components/knowledge/faq-editor'
import { FAQList } from '@/components/knowledge/faq-list'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Plus, FileText } from 'lucide-react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

export function Editor() {
  const [showEditor, setShowEditor] = useState(false)
  const [selectedFAQ, setSelectedFAQ] = useState<any>(null)

  // Handle Escape key to close editor
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showEditor) {
        handleCancel()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [showEditor])

  const handleNewFAQ = () => {
    setSelectedFAQ(null)
    setShowEditor(true)
  }

  const handleEditFAQ = (faq: any) => {
    setSelectedFAQ(faq)
    setShowEditor(true)
  }

  const handleSave = () => {
    setShowEditor(false)
    setSelectedFAQ(null)
  }

  const handleCancel = () => {
    setShowEditor(false)
    setSelectedFAQ(null)
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-light tracking-tight text-foreground">FAQ Editor</h1>
          <p className="text-muted-foreground">
            Erstellen und bearbeiten Sie FAQ-Einträge mit dem Rich-Text-Editor
          </p>
        </div>
        {!showEditor && (
          <Button onClick={handleNewFAQ} className="gap-2">
            <Plus className="h-4 w-4" />
            Neue FAQ erstellen
          </Button>
        )}
      </div>

      {showEditor ? (
        <FAQEditor
          faq={selectedFAQ}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      ) : (
        <Tabs defaultValue="all" className="space-y-4">
          <TabsList className="grid w-full grid-cols-4 max-w-[600px]">
            <TabsTrigger value="all">Alle FAQs</TabsTrigger>
            <TabsTrigger value="draft">Entwürfe</TabsTrigger>
            <TabsTrigger value="review">In Prüfung</TabsTrigger>
            <TabsTrigger value="approved">Genehmigt</TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Alle FAQ-Einträge
                </CardTitle>
              </CardHeader>
              <CardContent>
                <FAQList
                  onEditFAQ={handleEditFAQ}
                  enableBulkOperations={false}
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="draft" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  FAQ-Entwürfe
                </CardTitle>
              </CardHeader>
              <CardContent>
                <FAQList
                  onEditFAQ={handleEditFAQ}
                  enableBulkOperations={false}
                  filter={{ status: 'draft' }}
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="review" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  FAQs in Prüfung
                </CardTitle>
              </CardHeader>
              <CardContent>
                <FAQList
                  onEditFAQ={handleEditFAQ}
                  enableBulkOperations={false}
                  filter={{ status: 'review' }}
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="approved" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Genehmigte FAQs
                </CardTitle>
              </CardHeader>
              <CardContent>
                <FAQList
                  onEditFAQ={handleEditFAQ}
                  enableBulkOperations={false}
                  filter={{ status: 'approved' }}
                />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  )
}